export const normalizeUpdateProfilePayload = ({
  data,
  roleId,
  available,
  fromDate,
  toDate,
  email_verify_string,
  selectedResume,
  selectedFile,
}) => {
  let payload = { ...data };

  payload.step_number = data.step_number || "step_1";
  payload.type = "profile";
  payload.role_id = roleId;
  payload.role_type = roleId;
  payload.email_verify_string = email_verify_string;

  // Normalize boolean fields
  payload = normalizeBooleanFields(payload);

  // Handle availability
  if ("availability" in payload) {
    payload.availability = available;
  }

  // Handle subarea and mainArea
  if (payload.subarea) payload.subareas = payload.subarea;
  if (payload.mainArea) payload.domains = payload.mainArea;

  // Cleanup payload fields
  payload = cleanupPayloadFields(payload);

  // Handle resume
  payload = handleResumeInPayload(payload, selectedResume);

  // Handle image
  payload = handleImageInPayload(payload, selectedFile);

  // Handle availability dates
  payload = handleAvailabilityDates(payload, available, fromDate, toDate);

  return payload;
};

const normalizeBooleanFields = (payload) => {
  const booleanKeys = [
    "mn_open_to_talk",
    "is_privacy",
    "is_terms",
    "my_consent",
    "mn_loking_for_coach",
    "mf_mental_health",
    "mf_certified_coach",
    "lgbtq_discussion",
    "is_disclaimer",
    "training_video",
  ];

  const newPayload = { ...payload };
  for (const key of booleanKeys) {
    if (key in newPayload) {
      newPayload[key] = newPayload[key] ? 1 : 0;
    }
  }
  return newPayload;
};

const cleanupPayloadFields = (payload) => {
  const newPayload = { ...payload };

  if (
    newPayload.mainArea === "undefined" ||
    newPayload.mainArea === "null" ||
    !newPayload.mainArea
  ) {
    delete newPayload.mainArea;
  }

  if (
    !newPayload.domains ||
    newPayload.domains === "undefined" ||
    newPayload.domains === "null" ||
    newPayload.domains === null
  ) {
    delete newPayload.domains;
  }

  if (
    newPayload.language === "undefined" ||
    newPayload.language === "null" ||
    !newPayload.language
  ) {
    delete newPayload.language;
  }

  return newPayload;
};

const handleResumeInPayload = (payload, selectedResume) => {
  const newPayload = { ...payload };
  if (newPayload.resume) {
    if (Array.isArray(newPayload.resume) && newPayload.resume.length > 0) {
      newPayload.resume = newPayload.resume[0];
    } else if (selectedResume && typeof selectedResume === "string") {
      newPayload.resume = selectedResume;
    }
  } else if (selectedResume && typeof selectedResume !== "string") {
    newPayload.resume = selectedResume;
  } else {
    delete newPayload.resume;
  }
  return newPayload;
};

const handleImageInPayload = (payload, selectedFile) => {
  const newPayload = { ...payload };

  if (typeof selectedFile === "string") {
    const { image, ...rest } = newPayload;
    return rest;
  }

  newPayload.image = selectedFile;
  return newPayload;
};


const handleAvailabilityDates = (payload, available, fromDate, toDate) => {
  const newPayload = { ...payload };
  if (newPayload?.availability === 2) {
    newPayload.availability = 2;
    newPayload.from_date = fromDate;
    newPayload.to_date = toDate;
  } else {
    newPayload.availability = 1;
    newPayload.from_date = null;
    newPayload.to_date = null;
  }
  return newPayload;
};

// Board roles serialization functions
export const normalizeDateFields = (data) => {
  const normalized = { ...data };

  // Normalize board_roles dates if they exist
  if (normalized.board_roles && Array.isArray(normalized.board_roles)) {
    normalized.board_roles = normalized.board_roles.map(role => ({
      ...role,
      rep_start_date: role.rep_start_date || "",
      rep_end_date: role.rep_end_date || ""
    }));
  }

  return normalized;
};

export const serializeBoardRoles = (boardRoles) => {
  if (!boardRoles || !Array.isArray(boardRoles) || boardRoles.length === 0) {
    return "";
  }

  // Convert array of objects to JSON string for API
  return JSON.stringify(boardRoles);
};

export const deserializeBoardRoles = (boardRolesData) => {
  if (!boardRolesData) {
    return [];
  }

  // If it's already an array, return it with actual is_currently_working value from API
  if (Array.isArray(boardRolesData)) {
    // Remove id field from each role and use actual is_currently_working value
    return boardRolesData.map(({ id, ...role }) => ({
      ...role,
      // Use the actual is_currently_working value from API
      is_currently_working: !!role.is_currently_working,
    }));
  }

  // If it's a string, try to parse it
  if (typeof boardRolesData === 'string') {
    try {
      const parsed = JSON.parse(boardRolesData);
      if (Array.isArray(parsed)) {
        return parsed.map(({ id, ...role }) => ({
          ...role,
          // Use the actual is_currently_working value from API
          is_currently_working: !!role.is_currently_working,
        }));
      }
    } catch (e) {
      console.error('Error parsing board_roles:', e);
    }
  }

  return [];
};

const appendImage = (formData, payload, selectedFile) => {
  if (payload.image instanceof FileList && payload.image.length > 0) {
    formData.append("image", payload.image[0]);
  } else if (payload.image instanceof File) {
    formData.append("image", payload.image);
  } else if (selectedFile instanceof File) {
    formData.append("image", selectedFile);
  }

  if (
    "image" in payload &&
    !(payload.image instanceof File || payload.image instanceof FileList)
  ) {
    delete payload.image;
  }
};

const buildMergedKeys = (registerStepFields) => {
  const registerStep = registerStepFields?.[0]?.reg_feilds || [];

  const allowedKeys = registerStep.map((field) => field.reg_key);
  const filteredKeys = allowedKeys.filter((key) => key !== "password");

  let mergedKeys = [
    ...filteredKeys,
    "step_number",
    "type",
    "role_id",
    "role_type",
    "email_verify_string",
  ];

  if (allowedKeys.includes("domains")) {
    mergedKeys = [...mergedKeys, "subareas", "subarea", "mainArea"];
  }

  if (allowedKeys.includes("availability")) {
    mergedKeys = [...mergedKeys, "from_date", "to_date"];
  }

  const hasBoardRoleFields =
    allowedKeys.includes("rep_company_name") &&
    allowedKeys.includes("rep_board_title") &&
    allowedKeys.includes("rep_start_date") &&
    allowedKeys.includes("rep_end_date");

  if (hasBoardRoleFields) {
    mergedKeys.push("board_roles");
  }

  return mergedKeys;
};

const normalizeBoardArrayFields = (payload) => {
  const boardArrayFields = [
    "board_entity_type",
    "board_entity_type_2",
    "course_completed",
    "past_board_roles",
    "accredited_director",
    "indicate_board_experience_level",
  ];

  for (const field of boardArrayFields) {
    if (Array.isArray(payload[field])) {
      payload[field] = payload[field].join(",");
    }
  }
};

const normalizeBoardRoles = (payload) => {
  if (!Array.isArray(payload.board_roles)) return;

  const cleanedBoardRoles = payload.board_roles.map((role) => ({
    ...role,
    rep_end_date: role.is_currently_working
      ? ""
      : role.rep_end_date || "",
    is_currently_working: !!role.is_currently_working,
  }));

  payload.board_roles = JSON.stringify(cleanedBoardRoles);
};

const normalizeExperienceLevels = (payload) => {
  if (Array.isArray(payload.experience_levels)) {
    payload.experience_levels = payload.experience_levels.join(",");
  }
};

const appendFormData = (formData, payload, keys) => {
  for (const key of keys) {
    const value = payload[key];

    if (value !== undefined && value !== null && value !== "undefined") {
      formData.append(key, value === "" ? "" : value);
    }
  }
};

export const buildUpdateProfileFormData = ({
  data,
  registerStepFields,
  selectedFile,
}) => {
  const formData = new FormData();
  const payload = { ...data };

  appendImage(formData, payload, selectedFile);

  const mergedKeys = buildMergedKeys(registerStepFields);

  normalizeBoardArrayFields(payload);
  normalizeBoardRoles(payload);
  normalizeExperienceLevels(payload);

  appendFormData(formData, payload, mergedKeys);

  return formData;
};

export const buildValidationRules = (validations, is_required, reg_key, label = "") => {
  const rules = {};

  for (const validation of validations) {
    switch (validation?.key) {
      case "required":
        if (is_required === 0) {
          break;
        } else {
          rules.required =
            validation.message
              ?.replaceAll(/<[^>]*>/g, "")
              ?.replaceAll("*", "")
              ?.trim() ||
            `${label} is required`;
          break;
        }
      case "min":
      case "minlength":
        rules.minLength = {
          value: validation.length,
          message: validation.message,
        };
        break;
      case "max":
      case "maxlength":
        rules.maxLength = {
          value: validation.length,
          message: validation.message,
        };
        break;
      case "pattern":
        rules.pattern = {
          value: new RegExp(validation?.regex?.slice(1, -1)),
          message: validation.message,
        };
        break;
      case "minCount":
        rules.validate = {
          ...rules.validate,
          minCount: (value) => {
            if (Array.isArray(value) && value.length < validation.length) {
              return validation.message;
            }
            return true;
          },
        };
        break;

      case "maxCount":
        rules.validate = {
          ...rules.validate,
          maxCount: (value) => {
            if (Array.isArray(value) && value.length > validation.length) {
              return validation.message;
            }
            return true;
          },
        };
        break;

      case "size":
        rules.validate = {
          ...rules.validate,
          fileSize: (files) =>
            files?.length > 0
              ? files[0]?.size < validation?.length * 1024 ||
              validation?.message ||
              "File size exceeds the allowed limit"
              : true,
        };
        break;
      case "mimes": {
        const allowedMimeTypes = reg_key
          ? validation?.types?.map((ext) => `application/${ext}`)
          : validation?.types?.map((ext) => `image/${ext}`);
        rules.validate = {
          ...rules.validate,
          fileType: (files) => {
            if (files?.length > 0) {
              return (
                allowedMimeTypes?.includes(files[0].type) ||
                validation.message ||
                "Invalid file type"
              );
            }
            return true;
          },
        };
        break;
      }
      default:
        break;
    }
  };

  // Handle case where field is required but no specific "required" validation rule exists
  if (is_required === 1 && !rules.required) {
    rules.required = "This field is required";
  }
  return rules;
};

const applyBasicInfo = (userData, actions) => {
  const {
    setRoleId,
    setDob,
    setSelectedObjective,
    getObjectiveData,
    registerSetting,
    setEmailVerifyString,
    setText,
    setResumeText,
    setSelectedResume
  } = actions;

  setRoleId(userData?.role_id);
  setDob(userData?.dob);

  setSelectedObjective(
    userData?.objective ? userData.objective.split(",") : []
  );

  getObjectiveData(userData?.role_id);
  registerSetting(userData?.role_id);

  setEmailVerifyString(userData.email_verify_string);
  setText(userData?.about);

  setResumeText(
    userData?.resume ? userData.resume.split("resume/")[1] : ""
  );

  setSelectedResume(userData?.resume);
};

const applyExperience = (userData, { setExperience, setValue }) => {
  if (!userData?.experience_levels) return;

  const ids = userData.experience_levels.map((item) => item.id);

  setExperience(ids);
  setValue("experience_levels", ids, { shouldDirty: true });
};

const applyDomains = (userData, actions) => {
  const { setMainArea, setSelectedArea, setValue, clearErrors } = actions;

  const main = userData?.domains ? userData.domains.split(",") : [];
  const sub = userData?.subareas ? userData.subareas.split(",") : [];

  setMainArea(main);
  setSelectedArea(sub);

  setValue("mainArea", main);
  setValue("subarea", sub);
  setValue("domains", main);

  clearErrors("mainArea");
  clearErrors("domains");
};

const applyImage = (userData, { setImg, placeholder, setSelectedFile }) => {
  setImg({
    src: userData?.image || placeholder,
    alt: "Upload an Image",
  });

  setSelectedFile(userData?.image);
};

const applyAvailability = (userData, actions) => {
  const { setAvailable, setShowDateDiv, setValue, checkFromDate, checkToDate } =
    actions;

  setAvailable(userData?.user_availability);

  if (userData?.user_availability === 2) {
    setShowDateDiv("block");
    setValue("availability", true);
    setValue("from_date", checkFromDate(userData?.user_availability_check?.start_time));
    setValue("to_date", checkToDate(userData?.user_availability_check?.end_time));
  } else {
    setShowDateDiv("none");
    setValue("availability", false);
    setValue("from_date", null);
    setValue("to_date", null);
  }
};

const parseNumbers = (value) =>
  value.split(",").map((id) => Number.parseInt(id.trim()));

const applyBoardFields = (userData, { setValue, clearErrors }) => {

  const numberFields = [
    "board_entity_type",
    "board_entity_type_2",
    "course_completed",
    "accredited_director",
    "indicate_board_experience_level"
  ];

  for (const field of numberFields) {
    if (userData?.[field]) {
      setValue(field, parseNumbers(userData[field]));
      clearErrors(field);
    }
  }

  if (userData?.board_roles_data) {
    const roles = deserializeBoardRoles(userData.board_roles_data);

    setValue("board_roles", roles);
    clearErrors("board_roles");
  }

  if (userData?.past_board_roles) {
    const roles = userData.past_board_roles.split(",").map((r) => r.trim());

    setValue("past_board_roles", roles);
    clearErrors("past_board_roles");
  }

  if (userData?.years_of_board_experience) {
    setValue(
      "years_of_board_experience",
      userData.years_of_board_experience.toString()
    );
    clearErrors("years_of_board_experience");
  }
};

const fetchBoardData = (userData, actions) => {
  const {
    dispatch,
    fetchCoursesCompleted,
    fetchBoardExperience,
    fetchBoardExperienceLevel,
    fetchBoardEntityTypeExperience,
    fetchAccreditedDirector,
    fetchBoardExperienceMentor
  } = actions;

  if (userData?.role_id !== "Mentor" && userData?.role_id !== "Mentees") return;

  dispatch(fetchCoursesCompleted());
  dispatch(fetchBoardExperience(userData?.role_id));
  dispatch(fetchBoardExperienceLevel(userData?.role_id));
  dispatch(fetchBoardEntityTypeExperience(userData?.role_id));
  dispatch(fetchAccreditedDirector(userData?.role_id));
  dispatch(fetchBoardExperienceMentor("Mentee"));
};

const applyLanguages = (userData, setValue) => {
  if (!userData?.language) return;

  const selectedLanguages = userData.language.split(",");
  setValue("language", selectedLanguages);
};

const applyUserMeta = (userData, { setEmployee, setGender, setCountry }) => {
  if (userData?.employee) {
    setEmployee(userData.employee);
  }

  if (userData?.gender) {
    setGender(userData.gender);
  }

  if (userData?.country) {
    setCountry(userData.country);
  }
};

export const applyUserProfileData = (params) => {
  const {
    userData,
    reset,
    setRoleId,
    setDob,
    setSelectedObjective,
    getObjectiveData,
    registerSetting,
    setEmailVerifyString,
    setText,
    setResumeText,
    setSelectedResume,
    setExperience,
    setValue,
    setMainArea,
    setSelectedArea,
    clearErrors,
    setImg,
    setAvailable,
    setShowDateDiv,
    checkFromDate,
    checkToDate,
    setSelectedFile,
    getExperienceData,
    setEmployee,
    setGender,
    setCountry,
    placeholder,
    dispatch,
    fetchCoursesCompleted,
    fetchBoardExperience,
    fetchBoardExperienceLevel,
    fetchBoardEntityTypeExperience,
    fetchAccreditedDirector,
    fetchBoardExperienceMentor,
  } = params;

  const { image, resume, ...rest } = userData;

  reset(rest);

  applyBasicInfo(userData, {
    setRoleId,
    setDob,
    setSelectedObjective,
    getObjectiveData,
    registerSetting,
    setEmailVerifyString,
    setText,
    setResumeText,
    setSelectedResume,
  });

  applyExperience(userData, { setExperience, setValue });

  applyDomains(userData, {
    setMainArea,
    setSelectedArea,
    setValue,
    clearErrors,
  });

  applyImage(userData, { setImg, placeholder, setSelectedFile });

  applyAvailability(userData, {
    setAvailable,
    setShowDateDiv,
    setValue,
    checkFromDate,
    checkToDate,
  });

  applyLanguages(userData, setValue);

  applyUserMeta(userData, { setEmployee, setGender, setCountry });

  applyBoardFields(userData, { setValue, clearErrors });

  fetchBoardData(userData, {
    dispatch,
    fetchCoursesCompleted,
    fetchBoardExperience,
    fetchBoardExperienceLevel,
    fetchBoardEntityTypeExperience,
    fetchAccreditedDirector,
    fetchBoardExperienceMentor,
  });

  getExperienceData(userData?.role_id);
};

export const handlePostProfileLoadEffects = ({
  setComponentReady,
  setInitialDataLoaded,
}) => {
  setTimeout(() => {
    setComponentReady(true);
    setInitialDataLoaded(true);
  }, 300);
};
